import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import { Search } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Pagination from "../../../Ui_Components/Pagination";
import { BASE_URL } from "../../../api/url";
import { getRecipeList } from "../../../../services/clientDetails";
import { calculateCPFRatio, formatCPFRatio } from "../../../Plans/Meal_Creation/CPFCalculator";
import { ReactComponent as VegSvg } from "../../../../assects/svg/Recipe/Veg.svg";
import { ReactComponent as NonVegSvg } from "../../../../assects/svg/Recipe/Non_Veg.svg";

// ─── API Response Types ───────────────────────────────────────────────────────
interface ApiRecipe {
  recipe_id: number;
  recipe_name: string;
  recipe_image: string;
  CPF: string;
  calories: string;
  carbs: string;
  adjprotein: string;
  fats: string;
  gl: string;
  recipe_method: string;
  recipe_of_servings: string;
  recipe_cooking_time: string;
  recipe_choose_category: string; // "Veg" | "Non-Veg"
  recipe_cooking_type: string;    // comma-separated, e.g. "Boiled, Stir fry"
  recipe_meal_time: string;       // comma-separated, e.g. "Lunch, Dinner"
  recipe_dish_category: string;
  cooked_qty: string;
  serving_size: string;
  "measurements unit": string;
}

interface ApiResponse {
  success: string;
  message: string;
  page: number;
  size: number;
  total_count: number;
  total_pages: number;
  data: ApiRecipe[];
}

// ─── Normalised shape used inside the component ───────────────────────────────
interface Recipe {
  recipe_id: number;
  recipe_name: string;
  recipe_image: string | null;
  is_veg: boolean;
  cooking_types: string[];
  meal_times: string[];
  serving_size: string;
  cooking_time: string;
  calories: number;
  carbs: number;
  protein: number;
  fat: number;
  cpf: string;
  raw: ApiRecipe;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const splitTrimmed = (str: string): string[] =>
  str
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const normaliseRecipe = (r: ApiRecipe): Recipe => ({
  recipe_id: r.recipe_id,
  recipe_name: r.recipe_name,
  recipe_image: r.recipe_image && r.recipe_image.trim() !== "" ? r.recipe_image : null,
  is_veg: r.recipe_choose_category?.toLowerCase() === "veg",
  cooking_types: splitTrimmed(r.recipe_cooking_type),
  meal_times: splitTrimmed(r.recipe_meal_time),
  serving_size: r.serving_size,
  cooking_time: r.recipe_cooking_time,
  calories: parseFloat(r.calories) || 0,
  carbs: parseFloat(r.carbs) || 0,
  protein: parseFloat(r.adjprotein) || 0,
  fat: parseFloat(r.fats) || 0,
  cpf: formatCPFRatio(
    calculateCPFRatio(
      parseFloat(r.carbs) || 0,
      parseFloat(r.adjprotein) || 0,
      parseFloat(r.fats) || 0,
      parseFloat(r.calories) || 0
    ).ratio
  ),
  raw: r,
});

const ITEMS_PER_PAGE = 10;

// ─── Shimmer styles (matches MealPlanTab) ─────────────────────────────────────
const shimmerStyles = `
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  .shimmer {
    animation: shimmer 2s infinite;
    background: linear-gradient(
      to right,
      #f0f0f0 0%,
      #e0e0e0 20%,
      #f0f0f0 40%,
      #f0f0f0 100%
    );
    background-size: 1000px 100%;
  }
`;

// ─── Canvas text measurement ──────────────────────────────────────────────────
let _canvas: HTMLCanvasElement | null = null;
function measureTextWidth(text: string, font: string): number {
  if (!_canvas) _canvas = document.createElement("canvas");
  const ctx = _canvas.getContext("2d")!;
  ctx.font = font;
  return ctx.measureText(text).width;
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────
interface TooltipProps {
  items: string[];
  anchorRef: React.RefObject<HTMLSpanElement | null>;
  visible: boolean;
}

const ItemTooltip: React.FC<TooltipProps> = ({ items, anchorRef, visible }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!visible || !anchorRef.current || !tooltipRef.current) return;
    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 6;
    let top = anchorRect.bottom + gap + window.scrollY;
    let left = anchorRect.left + window.scrollX;
    if (anchorRect.bottom + gap + tooltipRect.height > window.innerHeight)
      top = anchorRect.top - tooltipRect.height - gap + window.scrollY;
    if (left + tooltipRect.width > window.innerWidth - 8)
      left = window.innerWidth - tooltipRect.width - 8 + window.scrollX;
    setPos({ top, left });
  }, [visible, anchorRef, items]);

  if (!visible) return null;

  return (
    <div
      ref={tooltipRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: 9999,
        border: "0.5px solid #F3F4F6",
      }}
      className="bg-white rounded-lg px-2 py-2 shadow-sm inline-flex max-w-[240px] pointer-events-none"
    >
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span
            key={i}
            className="text-[8px] 2xl:text-[10px] font-inter px-3 py-1 rounded-full whitespace-nowrap"
            style={{ backgroundColor: "#F0F0F0", color: "#1F1F1F" }}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── TruncatedList ────────────────────────────────────────────────────────────
const TruncatedList: React.FC<{ items: string[] }> = ({ items }) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [hovered, setHovered] = useState(false);
  const isTruncated = visibleCount < items.length;

 useLayoutEffect(() => {
  if (!containerRef.current || items.length === 0) return;

  const getFont = () => {
    if (window.innerWidth >= 1920) {
      return "16px Inter"; // 3xl
    }
    return "14px Inter";
  };

  const calculate = () => {
    const containerWidth = containerRef.current!.offsetWidth;
    if (!containerWidth) return;

    const font = getFont(); // 👈 dynamic font

    const sepWidth = measureTextWidth(", ", font);
    let usedWidth = 0;
    let count = 0;

    for (let i = 0; i < items.length; i++) {
      const itemWidth = measureTextWidth(items[i], font);
      const remaining = items.length - i - 1;
      const badgeWidth =
        remaining > 0 ? measureTextWidth(` +${remaining}`, font) : 0;
      const ahead = i === 0 ? 0 : sepWidth;

      if (usedWidth + ahead + itemWidth + badgeWidth <= containerWidth) {
        usedWidth += ahead + itemWidth;
        count = i + 1;
      } else break;
    }

    setVisibleCount(Math.max(1, count));
  };

  calculate();

  const ro = new ResizeObserver(calculate);
  ro.observe(containerRef.current!);

  return () => ro.disconnect();
}, [items]);

  const visible = items.slice(0, visibleCount);
  const remaining = items.length - visibleCount;

  return (
    <>
      <span
        ref={containerRef}
        onMouseEnter={() => isTruncated && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`block w-full overflow-hidden whitespace-nowrap text-[#1F1F1F] text-sm font-inter ${isTruncated ? "cursor-default" : ""}`}
      >
        {visible.join(", ")}
        {remaining > 0 && <span className="text-[#0DB5B5] font-medium"> +{remaining}</span>}
      </span>
      <ItemTooltip items={items} anchorRef={containerRef} visible={hovered && isTruncated} />
    </>
  );
};

// ─── VegIcon ──────────────────────────────────────────────────────────────────
const VegIcon: React.FC<{ isVeg: boolean }> = ({ isVeg }) => (
  <span
    title={isVeg ? "Vegetarian" : "Non-vegetarian"}
    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, flexShrink: 0, overflow: "visible" }}
  >
    {isVeg
      ? <VegSvg style={{ width: 16, height: 16, display: "block", overflow: "visible" }} />
      : <NonVegSvg style={{ width: 16, height: 16, display: "block", overflow: "visible" }} />
    }
  </span>
);

// ─── RecipeTab ────────────────────────────────────────────────────────────────
const RecipeTab: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  // ── pagination & search state ──
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ── API state ──
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─── Debounce search input (400ms) ───────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); // reset to page 1 on new search
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ─── Fetch from API ───────────────────────────────────────────────────────
  const fetchRecipes = useCallback(
    async (page: number, search: string) => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const json: ApiResponse = await getRecipeList(userId, page, ITEMS_PER_PAGE, search);
        if (json.success !== "1") throw new Error(json.message || "Failed to fetch recipes");
        setRecipes(json.data.map(normaliseRecipe));
        setTotalCount(json.total_count);
        setTotalPages(json.total_pages);
      } catch (err: any) {
        setError(err.message ?? "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  // Fetch whenever page or debounced search term changes
  useEffect(() => {
    fetchRecipes(currentPage, debouncedSearch);
  }, [fetchRecipes, currentPage, debouncedSearch]);

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowClick = (recipe: Recipe) => {
    navigate(`/client/${userId}/recipe/${recipe.recipe_id}`, {
      state: { recipe },
    });
  };

  // ─── Recipe image ─────────────────────────────────────────────────────────
  const getRecipeImage = (recipe: Recipe) => {
    const src = recipe.recipe_image
      ? recipe.recipe_image.startsWith("http")
        ? recipe.recipe_image
        : `${BASE_URL}${recipe.recipe_image}`
      : null;

    if (src) {
      return (
        <img
          src={src}
          alt={recipe.recipe_name}
          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
        />
      );
    }
    return (
      <div className="w-10 h-10 rounded-lg bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M8 16l2.5-3.5L13 15l2-2.5L18 16H8z" fill="#9CA3AF" />
          <circle cx="9.5" cy="10.5" r="1.5" fill="#9CA3AF" />
        </svg>
      </div>
    );
  };

  const borderClass = (index: number) =>
    index !== recipes.length - 1 ? "border-b border-[#111C2D1A]" : "";

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{shimmerStyles}</style>
<div className="pt-3"/>
      <div className="h-full bg-white rounded-[6px] shadow-sm flex flex-col border-[0.5px] border-[#F3F4F6] overflow-hidden">

        {/* Header */}
        <div className="flex-shrink-0 p-4 sm:px-6 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            Total recipe list ({totalCount})
          </h3>
          <div className="relative w-full sm:w-auto">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm"
            />
            <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>
        </div>

        {/* Column headers */}
        <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5 custom-scrollbar-overlay">
          <table className="w-full min-w-[700px] table-fixed">
            <thead>
              <tr>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs 3xl:text-sm font-inter font-normal w-[24%]">Recipe name</th>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs 3xl:text-sm font-inter font-normal w-[15%]">Cooking type</th>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs 3xl:text-sm font-inter font-normal w-[15%]">Meal time</th>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs 3xl:text-sm font-inter font-normal w-[12%]">Serving size</th>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs 3xl:text-sm font-inter font-normal w-[14%]">Cooking time</th>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs 3xl:text-sm font-inter font-normal w-[11%]">Cal</th>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs 3xl:text-sm font-inter font-normal w-[9%]">C:P:F</th>
              </tr>
            </thead>
          </table>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">

          {/* ── Loading shimmer ── */}
          {loading && (
            <div className="h-full shimmer" />
          )}

          {/* ── Error state ── */}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center h-full py-16 gap-3">
              <p className="text-red-400 text-sm font-inter">{error}</p>
              <button
                onClick={() => fetchRecipes(currentPage, debouncedSearch)}
                className="text-sm text-[#0DB5B5] underline font-inter"
              >
                Retry
              </button>
            </div>
          )}

          {/* ── Empty state ── */}
          {!loading && !error && recipes.length === 0 && (
            <div className="flex items-center justify-center h-full py-16">
              <p className="text-gray-400 text-sm 3xl:text-base font-inter">
                {debouncedSearch ? "No recipes match your search" : "No recipes found"}
              </p>
            </div>
          )}

          {/* ── Data rows ── */}
          {!loading && !error && recipes.length > 0 && (
            <table className="w-full min-w-[700px] table-fixed">
              <tbody>
                {recipes.map((recipe, index) => (
                  <tr
                    key={recipe.recipe_id}
                    onClick={() => handleRowClick(recipe)}
                    className="hover:bg-[#F9FEFE] transition-colors cursor-pointer"
                  >
                    <td className={`py-3 sm:py-4 px-4 sm:px-6 w-[24%] ${borderClass(index)}`}>
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        {getRecipeImage(recipe)}
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-medium font-inter text-[#1F1F1F] text-sm 3xl:text-base truncate">
                            {recipe.recipe_name}
                          </span>
                          <VegIcon isVeg={recipe.is_veg} />
                        </div>
                      </div>
                    </td>
                    <td className={`py-3 sm:py-4 px-4 sm:px-6  text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[15%] ${borderClass(index)}`}>
                      <TruncatedList items={recipe.cooking_types} />
                    </td>
                    <td className={`py-3 sm:py-4 px-4 sm:px-6  text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[15%] ${borderClass(index)}`}>
                      <TruncatedList items={recipe.meal_times} />
                    </td>
                    <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[12%] ${borderClass(index)}`}>
                      {recipe.serving_size}
                    </td>
                    <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[14%] ${borderClass(index)}`}>
                      {recipe.cooking_time}
                    </td>
                    <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[11%] ${borderClass(index)}`}>
                      {recipe.calories.toFixed(2)}kcal
                    </td>
                    <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[9%] ${borderClass(index)}`}>
                      {recipe.cpf}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination — hidden while loading */}
        {!loading && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            resultCount={recipes.length}
          />
        )}
      </div>
      
    </>
  );
};

export default RecipeTab;